/**
 * Dias úteis do mês: segunda a sexta, menos feriados (regra H).
 * Base do "ritmo" (regra I) e da "projeção" (regra J).
 * O "mês" pode ser um período diferente do calendário (fechamento especial,
 * dominio/fechamento.ts): por isso a conta principal recebe o período.
 *
 * Lê: a lista de feriados que recebe (normalmente dominio/feriados.ts).
 * Usado por: dominio/painel.ts.
 */
import { datasDoMes, datasDoPeriodo, diaDaSemana, limitesDoMes } from './datas.ts';
import type { DataIso, Mes } from './tipos.ts';

export function ehDiaUtil(data: DataIso, feriados: ReadonlySet<string>): boolean {
  const semana = diaDaSemana(data);
  return semana !== 0 && semana !== 6 && !feriados.has(data);
}

export function diasUteisDoMes(mes: Mes, feriados: ReadonlySet<string>): DataIso[] {
  return datasDoMes(mes).filter((data) => ehDiaUtil(data, feriados));
}

/**
 * Quantos dias úteis o mês tem e quantos já passaram até a data de referência
 * (incluindo ela). Sem data de referência, nenhum dia passou ainda.
 *
 * Exemplo (outubro/2026, referência 09/10): { passados: 7, total: 21 }.
 */
export function contarDiasUteis(
  mes: Mes,
  referencia: DataIso | null,
  feriados: ReadonlySet<string>,
): { passados: number; total: number } {
  return contarDiasUteisNoPeriodo(limitesDoMes(mes), referencia, feriados);
}

/** O mesmo, para um período qualquer (ex.: Bauny 09/2026 = 01/09 a 01/10). */
export function contarDiasUteisNoPeriodo(
  periodo: { inicio: DataIso; fim: DataIso },
  referencia: DataIso | null,
  feriados: ReadonlySet<string>,
): { passados: number; total: number } {
  const dias = datasDoPeriodo(periodo.inicio, periodo.fim).filter((data) => ehDiaUtil(data, feriados));
  const passados = referencia === null ? 0 : dias.filter((data) => data <= referencia).length;
  return { passados, total: dias.length };
}
